/**
 * Fuel Variant Definitions — Static Reference Data
 *
 * Calorific values, efficiencies, and units for all 16 heating fuel variants.
 * Based on Polish standards: URE tariffs, POBE methodology, PN-EN 15459.
 *
 * Sources:
 * - URE (Urząd Regulacji Energetyki) — gas/electricity tariffs
 * - POBE (Polska Organizacja Branży Energetycznej) — heating cost methodology
 * - Ministerstwo Rozwoju — energy certificate calculation methods
 * - PN-EN 14825:2018 — seasonal performance factor for heat pumps
 */

import type { FuelVariantDefinition, FuelVariantId } from '../types/heatingCost.types';

/**
 * All 16 fuel variant definitions.
 * Efficiencies represent annual averages (SPF for heat pumps, η for boilers).
 * Calorific values from official Polish data (kb.pl, POBE).
 */
export const FUEL_VARIANTS: Record<FuelVariantId, FuelVariantDefinition> = {
  // === Natural Gas (3 variants) ===
  gas_old_boiler: {
    id: 'gas_old_boiler',
    category: 'natural_gas',
    label: 'Gaz ziemny',
    boilerType: 'kocioł starego typu',
    calorificValue: 1.0, // price already in zł/kWh
    calorificUnit: '-',
    efficiency: 0.70,
    efficiencyUnit: '%',
    vatRate: 0.23,
    fuelUnit: 'kWh',
    consumptionUnit: 'kWh/rok',
  },
  gas_traditional_boiler: {
    id: 'gas_traditional_boiler',
    category: 'natural_gas',
    label: 'Gaz ziemny',
    boilerType: 'kocioł tradycyjny',
    calorificValue: 1.0,
    calorificUnit: '-',
    efficiency: 0.88,
    efficiencyUnit: '%',
    vatRate: 0.23,
    fuelUnit: 'kWh',
    consumptionUnit: 'kWh/rok',
  },
  gas_condensing_boiler: {
    id: 'gas_condensing_boiler',
    category: 'natural_gas',
    label: 'Gaz ziemny',
    boilerType: 'kocioł kondensacyjny',
    calorificValue: 1.0,
    calorificUnit: '-',
    efficiency: 1.03,
    efficiencyUnit: '%',
    vatRate: 0.23,
    fuelUnit: 'kWh',
    consumptionUnit: 'kWh/rok',
  },

  // === LPG (1 variant) ===
  lpg_condensing_boiler: {
    id: 'lpg_condensing_boiler',
    category: 'lpg',
    label: 'LPG',
    boilerType: 'kocioł kondensacyjny',
    calorificValue: 6.66, // kWh/litr (propan)
    calorificUnit: 'kWh/litr',
    efficiency: 1.03,
    efficiencyUnit: '%',
    vatRate: 0.23,
    fuelUnit: 'litr',
    consumptionUnit: 'litr/rok',
  },

  // === Heating Oil (2 variants) ===
  oil_traditional_boiler: {
    id: 'oil_traditional_boiler',
    category: 'heating_oil',
    label: 'Olej opałowy',
    boilerType: 'kocioł tradycyjny',
    calorificValue: 10.22, // kWh/litr
    calorificUnit: 'kWh/litr',
    efficiency: 0.90,
    efficiencyUnit: '%',
    vatRate: 0.23,
    fuelUnit: 'litr',
    consumptionUnit: 'litr/rok',
  },
  oil_condensing_boiler: {
    id: 'oil_condensing_boiler',
    category: 'heating_oil',
    label: 'Olej opałowy',
    boilerType: 'kocioł kondensacyjny',
    calorificValue: 10.22,
    calorificUnit: 'kWh/litr',
    efficiency: 1.00,
    efficiencyUnit: '%',
    vatRate: 0.23,
    fuelUnit: 'litr',
    consumptionUnit: 'litr/rok',
  },

  // === Coal (2 variants) ===
  coal_stoker: {
    id: 'coal_stoker',
    category: 'coal',
    label: 'Węgiel',
    boilerType: 'kocioł zasypowy, miałowy',
    calorificValue: 6.38, // kWh/kg
    calorificUnit: 'kWh/kg',
    efficiency: 0.65,
    efficiencyUnit: '%',
    vatRate: 0.23,
    fuelUnit: 'tona',
    consumptionUnit: 'ton/rok',
  },
  coal_ecopea: {
    id: 'coal_ecopea',
    category: 'coal',
    label: 'Węgiel',
    boilerType: 'z podajnikiem, "ekogroszek"',
    calorificValue: 7.22, // kWh/kg
    calorificUnit: 'kWh/kg',
    efficiency: 0.75,
    efficiencyUnit: '%',
    vatRate: 0.23,
    fuelUnit: 'tona',
    consumptionUnit: 'ton/rok',
  },

  // === Wood (2 variants) ===
  wood_log_boiler: {
    id: 'wood_log_boiler',
    category: 'wood',
    label: 'Drewno',
    boilerType: 'kocioł na drewno - buk',
    calorificValue: 3.80, // kWh/kg
    calorificUnit: 'kWh/kg',
    efficiency: 0.80,
    efficiencyUnit: '%',
    vatRate: 0.23,
    fuelUnit: 'm.p.',
    consumptionUnit: 'm.p./rok',
  },
  wood_pellet_boiler: {
    id: 'wood_pellet_boiler',
    category: 'wood',
    label: 'Drewno',
    boilerType: 'kocioł na pelet',
    calorificValue: 5.28, // kWh/kg
    calorificUnit: 'kWh/kg',
    efficiency: 0.85,
    efficiencyUnit: '%',
    vatRate: 0.23,
    fuelUnit: 'tona',
    consumptionUnit: 'ton/rok',
  },

  // === Electricity (4 variants) ===
  heat_pump_ground: {
    id: 'heat_pump_ground',
    category: 'electricity',
    label: 'Energia elektr.',
    boilerType: 'pompa ciepła - gruntowa',
    calorificValue: 1.0,
    calorificUnit: '-',
    efficiency: 4.0, // SPF (annual average COP), conservative
    efficiencyUnit: 'COP/SPF',
    vatRate: 0.23,
    fuelUnit: 'kWh',
    consumptionUnit: 'kWh/rok',
    electricityTariff: 'G11',
  },
  heat_pump_air: {
    id: 'heat_pump_air',
    category: 'electricity',
    label: 'Energia elektr.',
    boilerType: 'pompa ciepła - powietrzna',
    calorificValue: 1.0,
    calorificUnit: '-',
    efficiency: 2.7, // SPF (annual average COP), conservative for Polish climate
    efficiencyUnit: 'COP/SPF',
    vatRate: 0.23,
    fuelUnit: 'kWh',
    consumptionUnit: 'kWh/rok',
    electricityTariff: 'G11',
  },
  storage_heater: {
    id: 'storage_heater',
    category: 'electricity',
    label: 'Energia elektr.',
    boilerType: 'Piece Akumulacyjne',
    calorificValue: 1.0,
    calorificUnit: '-',
    efficiency: 1.0,
    efficiencyUnit: '-',
    vatRate: 0.23,
    fuelUnit: 'kWh',
    consumptionUnit: 'kWh/rok',
    electricityTariff: 'G12_night',
    dayNightRatio: { day: 0.0, night: 1.0 },
  },
  electric_boiler: {
    id: 'electric_boiler',
    category: 'electricity',
    label: 'Energia elektr.',
    boilerType: 'kocioł elektryczny',
    calorificValue: 1.0,
    calorificUnit: '-',
    efficiency: 1.0,
    efficiencyUnit: '-',
    vatRate: 0.23,
    fuelUnit: 'kWh',
    consumptionUnit: 'kWh/rok',
    electricityTariff: 'G11',
  },

  // === District Heating (1 variant) ===
  district_heating: {
    id: 'district_heating',
    category: 'district_heating',
    label: 'Ciepło sieciowe',
    boilerType: 'węzeł cieplny',
    calorificValue: 1.0, // price per kWh
    calorificUnit: '-',
    efficiency: 0.95,
    efficiencyUnit: '%',
    vatRate: 0.23,
    fuelUnit: 'kWh',
    consumptionUnit: 'kWh/rok',
  },
} as const;

/** Ordered list of all variant IDs for display purposes */
export const FUEL_VARIANT_ORDER: FuelVariantId[] = [
  'gas_old_boiler',
  'gas_traditional_boiler',
  'gas_condensing_boiler',
  'lpg_condensing_boiler',
  'oil_traditional_boiler',
  'oil_condensing_boiler',
  'coal_stoker',
  'coal_ecopea',
  'wood_log_boiler',
  'wood_pellet_boiler',
  'heat_pump_ground',
  'heat_pump_air',
  'storage_heater',
  'electric_boiler',
  'district_heating',
];

/** Category display order and labels */
export const FUEL_CATEGORY_LABELS: Record<string, string> = {
  natural_gas: 'Gaz ziemny',
  lpg: 'LPG',
  heating_oil: 'Olej opałowy',
  coal: 'Węgiel',
  wood: 'Drewno',
  electricity: 'Energia elektryczna',
  district_heating: 'Ciepło sieciowe',
};

/** Category colors for chart visualization */
export const FUEL_CATEGORY_COLORS: Record<string, string> = {
  natural_gas: '#2196F3',
  lpg: '#9C27B0',
  heating_oil: '#FF9800',
  coal: '#795548',
  wood: '#4CAF50',
  electricity: '#F44336',
  district_heating: '#607D8B',
};

/**
 * Weight of 1 m.p. (metr przestrzenny) of hardwood in kg.
 * Used to convert wood price per m.p. to price per kg.
 * Buk (beech): ~680 kg/m³, 1 m.p. ≈ ~340 kg (loosely stacked).
 */
export const WOOD_MP_TO_KG = 340;
